// Library search (audit T01). The old search was a substring match on the
// question, quick answer and category name: "vat threshold" found nothing while
// "What is the current VAT registration threshold?" sat in the library. This
// tokenises, expands the obvious synonyms, and scores — a question hit counting
// for more than a body hit — so close wording wins and a no-match means it
// really isn't there.

const STOP = new Set([
  "a", "an", "the", "is", "are", "do", "does", "did", "i", "my", "me", "we", "you", "your", "and", "or",
  "to", "for", "of", "in", "on", "at", "it", "if", "be", "can", "will", "what", "when", "how", "who",
  "with", "from", "need", "needs", "have", "has", "should", "about", "as", "but", "by", "that", "this",
]);

// Both directions: typing either word finds answers written with the other.
const SYNONYMS = [
  ["paye", "payroll", "employer", "rti"],
  ["mtd", "making", "digital", "itsa"],
  ["annual", "year-end", "yearend", "statutory", "final"],
  ["accounts", "accounting", "accountancy"],
  ["vat", "value-added"],
  ["threshold", "limit", "thresholds", "registration"],
  ["register", "registration", "registering", "signup"],
  ["cost", "price", "pricing", "fee", "fees", "charge", "charges", "quote"],
  ["deadline", "deadlines", "due", "date", "dates"],
  ["director", "directors", "shareholder"],
  ["sole", "self-employed", "selfemployed", "freelance"],
  ["bookkeeping", "bookkeeper", "books", "records"],
  ["reconcile", "reconciliation", "reconciliations"],
  ["solicitor", "solicitors", "law", "legal", "sra"],
  ["conveyancer", "conveyancers", "conveyancing", "clc"],
  ["forecast", "forecasting", "cashflow", "cash-flow"],
  ["switch", "switching", "move", "moving", "transfer"],
  ["tax", "taxes", "hmrc"],
];

const expand = (token) => {
  const out = new Set([token]);
  SYNONYMS.forEach((group) => {
    if (group.includes(token)) group.forEach((g) => out.add(g));
  });
  // Crude but effective stemming for the plurals the library is full of.
  if (token.endsWith("s") && token.length > 3) out.add(token.slice(0, -1));
  else out.add(`${token}s`);
  return out;
};

export const tokenise = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .split(/[^a-z0-9£%+-]+/)
    .map((t) => t.replace(/^-+|-+$/g, ""))
    .filter((t) => t && t.length > 1 && !STOP.has(t));

const bodyOf = (a) =>
  [a.quick_answer, (a.sections || []).map((s) => `${s.heading || ""} ${s.body || ""}`).join(" "), a.category_name]
    .join(" ");

export const scoreAnswer = (a, queryTokens) => {
  if (!queryTokens.length) return 0;
  const qTokens = new Set(tokenise(a.question));
  const bTokens = new Set(tokenise(bodyOf(a)));
  let score = 0;
  queryTokens.forEach((token) => {
    const variants = expand(token);
    const inQuestion = [...variants].some((v) => qTokens.has(v));
    const inBody = [...variants].some((v) => bTokens.has(v));
    if (inQuestion) score += 2;
    else if (inBody) score += 1;
  });
  // A whole phrase appearing in the question is the strongest signal there is.
  return score / (2 * queryTokens.length);
};

export const searchAnswers = (answers, term, { limit = 12, min = 0.34 } = {}) => {
  const tokens = tokenise(term);
  if (!tokens.length) return [];
  const phrase = String(term || "").toLowerCase().trim();
  return answers
    .map((a) => {
      let score = scoreAnswer(a, tokens);
      if (score && String(a.question || "").toLowerCase().includes(phrase)) score = Math.min(1, score + 0.25);
      return { a, score };
    })
    .filter((r) => r.score >= min)
    .sort((x, y) => y.score - x.score || x.a.question.length - y.a.question.length)
    .slice(0, limit)
    .map((r) => r.a);
};
